/**
 * One business-wide result becomes the words and numbers a card draws.
 *
 * The sibling of `insight-present.ts`, and it answers into the SAME `Presented`
 * type — which is what lets all six cards, the strip and the gallery rows be
 * reused without a line of change. A card takes a `Presented` and knows nothing
 * about where it came from.
 *
 * Two differences from the record presenter, both forced by the data.
 *
 *   the names   a record breakdown resolves its group keys through the entry
 *               titles of a directory. A business breakdown's keys are record
 *               ids, people and days, so the names come from the catalog and
 *               the catalogue, never from a query.
 *   the tone    a record reads direction off the sign of `signed_value`. Here
 *               the FLOW already says it: "Money out" is an outflow whether the
 *               number is large or small, so it wears the out colour and shows
 *               a positive number. Colour still only repeats what the label and
 *               the sign already say, because our green and red separate by
 *               4.4 Delta E under deuteranopia.
 */
import type { RecordSchemaResponse } from '@alluzo/shared';
import { formatWeekdayDay } from '@alluzo/shared';

import { formatMoney, formatNumber } from '@/lib/entry-format';
import { t, tData, tOr } from '@/lib/i18n';
import type { BusinessInsightLayoutEntry } from '@/lib/insight-business-layout';
import type {
  BusinessAggNode,
  BusinessFilter,
  BusinessGroupKey,
  BusinessInsightSpec,
  BusinessMeasure,
  BusinessOwnSpec,
  BusinessPlainMeasure,
} from '@/lib/insight-business-query';
import { businessFilterValues, isHostedSpec, isMoneyMeasure, ME } from '@/lib/insight-business-query';
import type { HopSchemas, InsightTone, Presented, PresentedSlice } from '@/lib/insight-present';
import { changeOf, describeInsight, presentInsight } from '@/lib/insight-present';
import type {
  BreakdownResult,
  InsightResult,
  InsightScope,
  PairResult,
  ScalarResult,
  SeriesResult,
} from '@/lib/insight-query';

/**
 * What the presenter needs besides the result.
 *
 * Everything here is already on the device: the names come from
 * `record_catalog`, the currency from the same pick the compiler used, and the
 * signed-in id from the cached identity. No query, so a card's words cost
 * nothing.
 */
export type BusinessPresentContext = {
  readonly currency: string | null;
  /** recordId to the tenant's own name for that record. */
  readonly recordNames: Readonly<Record<string, string>>;
  readonly meId: string | null;
  /**
   * A writer's id to their name. Empty until the members route exists, and the
   * presenter says "Someone else" rather than showing a uuid.
   */
  readonly writerNames?: Readonly<Record<string, string>>;
  /**
   * The cached schema of one record, for a card hosted here.
   *
   * A hosted card is a record's own question, so its words come from the record
   * presenter: the field labels, the measure's own noun, the currency of that
   * record. Only the cached schema holds those. A host whose schema has not been
   * pulled draws NOTHING rather than a number under a guessed name.
   */
  readonly schemaOf?: (recordId: string) => RecordSchemaResponse | null;
};

/**
 * The names a hosted card's own sentence may need, catalogue first.
 *
 * A combined card names the records its totals reach, and those are record ids —
 * which the record presenter cannot resolve on its own, because a record knows
 * only itself. The Dashboard holds the whole catalogue, so it hands it over.
 * The group keys win where the two ever collide, which they cannot: an entry id
 * is not a record id.
 */
const withRecordNames = (
  context: BusinessPresentContext,
  names: Readonly<Record<string, string>>,
): Readonly<Record<string, string>> => ({ ...context.recordNames, ...names });

/**
 * The schemas the host's own references reach.
 *
 * Built out of the context's own lookup rather than read from the cache: this
 * file is pure, and a module that reached the database could not be tested
 * under bun. The Dashboard hands `schemaOf` in for exactly this.
 */
const hopsOf = (
  context: BusinessPresentContext,
  schema: RecordSchemaResponse,
): HopSchemas => {
  const found = new Map<string, RecordSchemaResponse>();
  for (const field of schema.fields) {
    if (field.type !== 'reference' || field.targetRecordId === null) continue;
    if (found.has(field.targetRecordId)) continue;
    const held = context.schemaOf?.(field.targetRecordId) ?? null;
    if (held !== null) found.set(field.targetRecordId, held);
  }
  return found;
};

/** The schema a hosted card needs, or null when this device has not pulled it. */
const hostSchema = (
  context: BusinessPresentContext,
  spec: BusinessInsightSpec,
): RecordSchemaResponse | null =>
  isHostedSpec(spec) ? (context.schemaOf?.(spec.host) ?? null) : null;

/* ------------------------------------------------------------------ words */

const scopeWord = (scope: InsightScope): string => t(`insight.scope.${scope}`);

/** The word for what a pair compares against. */
const againstWord = (scope: Exclude<InsightScope, 'all'>): string => t(`insight.vs_${scope}`);

/**
 * What the measure is called.
 *
 * A sum needs no word of its own — "Money in" already means all of it. The
 * other three aggregates say which one they took.
 */
const measureWord = (spec: BusinessOwnSpec, context: BusinessPresentContext): string => {
  const measure = spec.measure;
  if (measure.agg === 'idle_days') return t('insight.biz.days_quiet');
  if (measure.agg === 'formula') return combinedWord(context, measure.expr);
  if (measure.agg === 'rows' && spec.kind === 'directory') return t('insight.biz.list_rows');
  return plainWord(measure);
};

/** One plain measure's word: "Money in", "Average money out", "Entries". */
export const plainWord = (measure: BusinessPlainMeasure): string => {
  if (measure.agg === 'rows') return t('insight.biz.entries');
  const flow = t(`insight.biz.flow.${measure.flow}`);
  if (measure.agg === 'sum') return flow;
  return `${t(`insight.biz.agg.${measure.agg}`)} ${flow.toLowerCase()}`;
};

/**
 * A combined card's words when nobody named it: each total, with its own
 * conditions, joined by the operators between them — "Money in only Daily
 * sales minus money out only Shop expenses".
 */
const combinedWord = (context: BusinessPresentContext, expr: BusinessAggNode): string => {
  let first = true;
  const say = (node: BusinessAggNode): string => {
    if (node.kind === 'agg') {
      const word = plainWord(node.measure);
      const said = first ? word : word.toLowerCase();
      first = false;
      const conditions = describeFilters(context, node.filters);
      return conditions === '' ? said : `${said} ${conditions}`;
    }
    if (node.kind === 'const') return String(node.value);
    if (node.kind === 'round') return say(node.of);
    const left = say(node.left);
    return `${left} ${t(`formula.op.${node.op}_word`)} ${say(node.right)}`;
  };
  return say(expr);
};

/** The tenant's own name for a record, or its id when the catalog has not landed. */
const recordNameOf = (context: BusinessPresentContext, recordId: string): string =>
  context.recordNames[recordId] ?? recordId;

/**
 * The word for one person.
 *
 * The signed-in person is "You", which is true and needs no server. Anybody
 * else is named if a name is known and called "Someone else" if not — a uuid on
 * a card is worse than an honest placeholder, and the names arrive the day the
 * members route does.
 */
const writerName = (context: BusinessPresentContext, id: string): string => {
  if (context.meId !== null && id === context.meId) return t('insight.biz.you');
  const known = context.writerNames?.[id];
  return known !== undefined && known !== '' ? known : t('insight.biz.someone');
};

/** A day, as a card shows it: "Mon 24 Aug". Read in UTC, as it was stored. */
const dayName = formatWeekdayDay;

/** The name of one group key's value. */
const groupName = (context: BusinessPresentContext, by: BusinessGroupKey, key: string): string => {
  if (by === 'record') return recordNameOf(context, key);
  if (by === 'creator') return writerName(context, key);
  if (by === 'day') return dayName(key);
  return tOr(`insight.biz.channel.${key}`, key);
};

/** A template's own word, or its id when this build does not know the template. */
const templateName = (id: string): string =>
  tOr(`template.${id.replaceAll('-', '_')}.label`, id);

/* -------------------------------------------------------------- conditions */

/** How many conditions a derived name spells out before it counts the rest. */
const CONDITIONS_IN_A_NAME = 2;

/**
 * One condition, in the words a shopkeeper would use.
 *
 * "without Staff Payouts", not "record is not Staff Payouts". A chip has one line
 * and the phrasing per key is short enough to be written out, so it is.
 *
 * Exported because the editor draws the same sentence on its chips. One
 * spelling, two screens.
 */
export const describeBusinessFilter = (
  context: BusinessPresentContext,
  filter: BusinessFilter,
): string => {
  const wordOf = (value: string): string =>
    filter.key === 'record'
      ? recordNameOf(context, value)
      : filter.key === 'template'
        ? templateName(value)
        : filter.key === 'creator'
          ? value === ME
            ? t('insight.biz.you').toLowerCase()
            : writerName(context, value)
          : tOr(`insight.biz.channel.${value}`, value);
  const words = businessFilterValues(filter).map(wordOf);
  // "Only A or B" is any of them; "without A and B" is none of them. Past two,
  // the rest are counted, as a record's condition counts them.
  const joiner = filter.op === 'not_in' ? t('insight.and') : t('filter.or');
  const spelled = words.slice(0, VALUES_IN_A_SENTENCE).join(` ${joiner} `);
  const rest = words.length - VALUES_IN_A_SENTENCE;
  const value = rest > 0 ? `${spelled} ${t('insight.more_values', { count: rest })}` : spelled;
  // "Any of" says what "is" says, over a list, so it reads with the same
  // sentence. The marker in each is named after the filter's own key —
  // `{{record}}`, `{{template}}`, `{{creator}}`, `{{channel}}` — so one line
  // fills all eight without a lookup table beside them.
  const op = filter.op === 'in' ? 'is' : filter.op === 'not_in' ? 'is_not' : filter.op;
  return t(`insight.biz.cond.${filter.key}.${op}`, { [filter.key]: value });
};

/** How many of a condition's several values a sentence names before it counts the rest. */
const VALUES_IN_A_SENTENCE = 2;

const describeFilters = (
  context: BusinessPresentContext,
  filters: readonly BusinessFilter[] | undefined,
): string => {
  if (filters === undefined || filters.length === 0) return '';
  const spelled = filters
    .slice(0, CONDITIONS_IN_A_NAME)
    .map((filter) => describeBusinessFilter(context, filter));
  const rest = filters.length - spelled.length;
  const tail = rest > 0 ? [t('insight.more_conditions', { count: rest })] : [];
  return [...spelled, ...tail].join(` ${t('insight.and')} `);
};

/* ------------------------------------------------------------------ labels */

/**
 * The card's own name. Three sources, in this order: what the person typed,
 * what the default called it, then a sentence built from the spec.
 */
export const businessLabelFor = (
  context: BusinessPresentContext,
  entry: Pick<BusinessInsightLayoutEntry, 'spec' | 'labelKey'> & { readonly title?: string | null },
  names: Readonly<Record<string, string>> = {},
): string => {
  if (entry.title !== undefined && entry.title !== null && entry.title !== '') return entry.title;
  const { spec } = entry;
  // A hosted card is a record's own question, so it says so in the record's own
  // words. With no schema there is nothing to build a sentence from, and the
  // record's name is the one true thing left to say.
  if (isHostedSpec(spec)) {
    const schema = hostSchema(context, spec);
    const hostName = recordNameOf(context, spec.host);
    if (schema === null) return entry.labelKey === null ? hostName : tData(entry.labelKey);
    if (entry.labelKey !== null) return tData(entry.labelKey);
    // A combined card already names the records it reads, so saying the host
    // again would read as "Deliveries, Deliveries minus Payments". Every other
    // hosted card is one record's question standing among several, and that name
    // is the first thing a person needs to know about it.
    const said = describeInsight(
      schema,
      spec.record,
      withRecordNames(context, names),
      hopsOf(context, schema),
    );
    return spec.record.measure.agg === 'formula' ? said : `${hostName}, ${said}`;
  }
  const conditions = describeFilters(context, spec.filters);
  if (entry.labelKey !== null) {
    const named = tData(entry.labelKey);
    return conditions === '' ? named : `${named}, ${conditions}`;
  }

  const base = ((): string => {
    // Idleness has one meaning, and "days quiet by record, all time" is not how
    // anybody says it.
    if (spec.measure.agg === 'idle_days') return t('insight.biz.quiet_records');
    if (spec.shape === 'series') {
      return `${measureWord(spec, context)}, ${t('insight.last_days', { days: spec.days })}`;
    }
    if (spec.shape === 'breakdown') {
      return `${measureWord(spec, context)} ${t(`insight.biz.by.${spec.by}`)}, ${scopeWord(spec.scope)}`;
    }
    if (spec.shape === 'scalar' && spec.leaderOf !== undefined) {
      return `${t(`insight.biz.top.${spec.leaderOf}`)}, ${scopeWord(spec.scope)}`;
    }
    // A comparison says so in its name. Without this a plain number and the
    // same number with its change read identically, and the gallery would
    // offer two cards that look like one.
    if (spec.shape === 'scalar_pair') {
      return `${measureWord(spec, context)}, ${scopeWord(spec.scope)} ${againstWord(spec.scope)}`;
    }
    return `${measureWord(spec, context)}, ${scopeWord(spec.scope)}`;
  })();

  return conditions === '' ? base : `${base}, ${conditions}`;
};

/** What a card draws, in one line, ignoring any name it was given. */
export const describeBusinessInsight = (
  context: BusinessPresentContext,
  spec: BusinessInsightSpec,
  names: Readonly<Record<string, string>> = {},
): string => businessLabelFor(context, { spec, labelKey: null }, names);

/* ----------------------------------------------------------------- numbers */

/**
 * A total over nothing is zero. Anything else over nothing is unknown.
 *
 * SQLite's `SUM` answers NULL when no row matched, which is not the same claim
 * as zero — and for an average, a smallest or a largest it really is "there is
 * no number", so those stay empty. But a SUM is arithmetic: money out today on
 * a day nothing went out IS zero, and `toTodayTotals` has always said so. Home
 * showed "Out today ₹0" before this strip existed, and it must keep showing it.
 *
 * A count needs no help: `COUNT` never answers NULL.
 */
const zeroIsAnAnswer = (measure: BusinessMeasure): boolean => measure.agg === 'sum';

const readValue = (measure: BusinessMeasure, value: number | null): number | null =>
  value === null && zeroIsAnAnswer(measure) ? 0 : value;

/**
 * A number, in the units the insight is in.
 *
 * Money only when the measure reads money AND the business has a currency.
 * Counting entries never produces rupees, and days quiet are days.
 */
const asAmount = (
  context: BusinessPresentContext,
  measure: BusinessMeasure,
  value: number | null,
): string => {
  const held = readValue(measure, value);
  if (held === null) return '';
  if (measure.agg === 'idle_days') {
    return held === 0
      ? t('insight.biz.today')
      : t('insight.biz.days', { days: held });
  }
  return isMoneyMeasure(measure) ? formatMoney(held, context.currency) : formatNumber(held);
};

/* -------------------------------------------------------------------- tone */

/**
 * Which way a number points.
 *
 * The FLOW says it, not the sign: "Money out" is money leaving whether the
 * number is 20 or 20,000, and it shows a positive number because that is what
 * `out` computes. `net` has no fixed direction, so it reads its own sign. A
 * count and a day count have no direction at all.
 *
 * Colour is the last of four signals here as it is everywhere else: the label
 * says the flow, the sign says the rest, and the colour only repeats them.
 */
const toneOf = (measure: BusinessMeasure, value: number | null): InsightTone => {
  if (measure.agg === 'rows' || measure.agg === 'idle_days') return 'neutral';
  // A combined card of counts has no direction; one of money reads as a net
  // does, by its sign.
  if (measure.agg === 'formula' && !isMoneyMeasure(measure)) return 'neutral';
  if (measure.agg !== 'formula' && measure.flow === 'in') return 'positive';
  if (measure.agg !== 'formula' && measure.flow === 'out') return 'negative';
  if (value === null || value === 0) return 'neutral';
  return value > 0 ? 'positive' : 'negative';
};

/**
 * Whether a rise in this measure is good news.
 *
 * More money in is good; more money out is not; a bigger net is good. A count
 * has no opinion, and neither has a day count.
 */
const risingIsGood = (measure: BusinessMeasure): boolean | null => {
  if (measure.agg === 'rows' || measure.agg === 'idle_days') return null;
  // A combined card is somebody's own arithmetic, so it holds no opinion on
  // which way is good news.
  if (measure.agg === 'formula') return null;
  return measure.flow !== 'out';
};

/* --------------------------------------------------------------- presenter */

const EMPTY = {
  change: null,
  progress: null,
  points: [] as readonly { day: string; value: number }[],
  latest: null,
  slices: [] as readonly PresentedSlice[],
  other: null,
} as const;

const presentScalar = (
  context: BusinessPresentContext,
  spec: Extract<BusinessOwnSpec, { shape: 'scalar' }>,
  result: ScalarResult,
): Pick<Presented, 'value' | 'tone' | 'empty' | 'latest'> => {
  const held = readValue(spec.measure, result.value);
  if (held === null) return { value: '', tone: 'neutral', empty: true, latest: null };
  const amount = asAmount(context, spec.measure, held);
  // A leader answers a NAME, with its number underneath: "Sales" over
  // "₹57,441". One number, one label, so it needs no card of its own. With no
  // rows there is no winner, and a zero under no name would be a card that
  // looks answered — so `result.key` is what decides, never the number.
  if (spec.leaderOf !== undefined && result.key !== null) {
    return {
      value: groupName(context, spec.leaderOf, result.key),
      tone: 'neutral',
      empty: false,
      latest: amount,
    };
  }
  return { value: amount, tone: toneOf(spec.measure, held), empty: false, latest: null };
};

const presentPair = (
  context: BusinessPresentContext,
  spec: Extract<BusinessOwnSpec, { shape: 'scalar_pair' }>,
  result: PairResult,
): Pick<Presented, 'value' | 'tone' | 'empty' | 'change' | 'progress' | 'latest'> => {
  if (result.value === null && result.against === null) {
    return { value: '', tone: 'neutral', empty: true, change: null, progress: null, latest: null };
  }
  const value = result.value ?? 0;
  const change = changeOf(result.value, result.against);
  const good = risingIsGood(spec.measure);
  return {
    value: asAmount(context, spec.measure, value),
    tone: toneOf(spec.measure, result.value),
    empty: false,
    change:
      change === null
        ? { percent: '', against: t('insight.no_compare'), tone: 'neutral' }
        : {
            percent: `${change.percent}%`,
            against: againstWord(spec.scope),
            // More money out is not good news, and more money in is. That one
            // rule covers every flow without knowing what the records are for.
            tone:
              good === null || change.percent === 0
                ? 'neutral'
                : change.rising === good
                  ? 'positive'
                  : 'negative',
          },
    // The TRUE ratio, never clamped: beating last month by three quarters is
    // 177%, and rounding it to 100% would be the card lying about the one
    // thing it exists to say. `MeterBar` clamps its own fill.
    progress:
      result.against === null || result.against === 0
        ? null
        : Math.abs(value) / Math.abs(result.against),
    latest: result.against === null ? null : asAmount(context, spec.measure, result.against),
  };
};

const presentSeries = (
  context: BusinessPresentContext,
  spec: Extract<BusinessOwnSpec, { shape: 'series' }>,
  result: SeriesResult,
): Pick<Presented, 'value' | 'tone' | 'empty' | 'points' | 'latest'> => {
  const total = result.points.reduce((sum, point) => sum + point.value, 0);
  const last = result.points.at(-1);
  return {
    value: asAmount(context, spec.measure, total),
    tone: 'neutral',
    // A flat line of zeros is not data: nothing landed in the window, and the
    // card should say so rather than draw a floor.
    empty: result.points.every((point) => point.value === 0),
    points: result.points,
    latest: last === undefined ? null : asAmount(context, spec.measure, last.value),
  };
};

const presentBreakdown = (
  context: BusinessPresentContext,
  spec: Extract<BusinessOwnSpec, { shape: 'breakdown' }>,
  result: BreakdownResult,
): Pick<Presented, 'value' | 'tone' | 'empty' | 'slices' | 'other'> => {
  if (result.slices.length === 0) {
    return { value: '', tone: 'neutral', empty: true, slices: [], other: null };
  }
  // Bars are drawn against the biggest row, not against the total: the point of
  // a ranked list is how the rows compare with each other.
  const largest = Math.max(...result.slices.map((slice) => Math.abs(slice.value)));
  const total =
    result.slices.reduce((sum, slice) => sum + slice.value, 0) + (result.other?.value ?? 0);
  return {
    // A total of "days quiet" is meaningless, so that card shows no headline.
    value: spec.measure.agg === 'idle_days' ? '' : asAmount(context, spec.measure, total),
    tone: 'neutral',
    empty: false,
    slices: result.slices.map((slice) => ({
      label: groupName(context, spec.by, slice.key),
      value: asAmount(context, spec.measure, slice.value),
      fraction: largest === 0 ? 0 : Math.abs(slice.value) / largest,
    })),
    other:
      result.other === null
        ? null
        : {
            label: `${t('insight.other')}, ${formatNumber(result.other.count)}`,
            value: asAmount(context, spec.measure, result.other.value),
          },
  };
};

/**
 * One layout entry plus its result, as everything a card needs to draw.
 *
 * A null result presents as empty rather than as an error. It has one cause
 * worth knowing: a money card on a business with no currency, which the
 * compiler refuses. The card draws its quiet placeholder and fills in the
 * moment the business keeps a record that carries money.
 */
export const presentBusinessInsight = (
  context: BusinessPresentContext,
  entry: BusinessInsightLayoutEntry,
  result: InsightResult | null,
  /** Entry id to title, for a hosted breakdown whose keys point at a list. */
  names: Readonly<Record<string, string>> = {},
): Presented => {
  const base = {
    id: entry.id,
    card: entry.card,
    span: entry.span,
    // The rectangle on the Dashboard's grid. The hosted entry below hands
    // `presentInsight` the same value, so the two agree.
    size: entry.size,
    label: businessLabelFor(context, entry, names),
    // A business card's own vocabulary has no recipe in it: money, days, rows.
    // The one card here that can be worked out is a hosted one, and the record
    // presenter below decides that for itself.
    worked: false,
    // The hook sets this, because only it knows whether the result it handed
    // over was a sample. See `insight-sample.ts`.
    sample: false,
  };

  // A hosted card is drawn by the RECORD presenter, with the host's own schema:
  // its field labels, its measure's noun, its currency. Nothing here could say
  // any of that, and a second presenter for one grammar would say it differently.
  if (isHostedSpec(entry.spec)) {
    const schema = hostSchema(context, entry.spec);
    if (schema === null) return { ...base, ...EMPTY, value: '', tone: 'neutral', empty: true };
    return presentInsight(
      schema,
      {
        id: entry.id,
        card: entry.card,
        spec: entry.spec.record,
        size: entry.size,
        span: entry.span,
        labelKey: entry.labelKey,
        title: entry.title,
      },
      result,
      withRecordNames(context, names),
      // The label is worked out HERE and handed over, because only this side
      // knows the record a hosted card belongs to.
      base.label,
      hopsOf(context, schema),
      // The business's currency, for a worked-out amount on a record with none.
      context.currency,
    );
  }

  if (result === null || result.shape !== entry.spec.shape) {
    return { ...base, ...EMPTY, value: '', tone: 'neutral', empty: true };
  }

  switch (entry.spec.shape) {
    case 'scalar':
      return { ...base, ...EMPTY, ...presentScalar(context, entry.spec, result as ScalarResult) };
    case 'scalar_pair':
      return { ...base, ...EMPTY, ...presentPair(context, entry.spec, result as PairResult) };
    case 'series':
      return { ...base, ...EMPTY, ...presentSeries(context, entry.spec, result as SeriesResult) };
    case 'breakdown':
      return {
        ...base,
        ...EMPTY,
        ...presentBreakdown(context, entry.spec, result as BreakdownResult),
      };
  }
};
